import { useEffect, useRef, useState } from "react";
import { useOperator } from "@/context/OperatorContext";
import { useAuth } from "@/context/AuthContext";
import api, { getToken } from "@/lib/api";
import { toast } from "sonner";
import FleetMap from "@/components/FleetMap";
import { Input } from "@/components/ui/input";
import { Radio, MapPin, Gauge, BatteryMedium, AlertTriangle, Loader2, Lock, Navigation, Power, Activity, Bike, Crosshair, Search, Zap, WifiOff, Clock, Target, RefreshCw, Home, Star } from "lucide-react";

const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const compass = (h) => (h == null ? null : COMPASS[Math.round(h / 45) % 8]);
const mins = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 60000 : null);

function ago(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function clock(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return "—"; }
}

function activeness(v) {
  if ((v.speed || 0) > 0) return { rank: 0, label: "Moving", cls: "bg-status-available/15 text-status-available" };
  if (v.ignition === "ON") return { rank: 1, label: "Idle", cls: "bg-status-overdue/15 text-status-overdue" };
  return { rank: 2, label: "Parked", cls: "bg-muted text-muted-foreground" };
}

const DEFAULT_CFG = { alerts_enabled: true, search_enabled: true, follow_enabled: true, pulse_enabled: true, idle_min: 15, stale_min: 20, speed_limit: 60, map_default: "main_hub" };

function computeAlerts(vehicles, cfg) {
  const out = [];
  vehicles.forEach((v) => {
    if (v.lat == null) return;
    const spd = v.speed || 0;
    const lastM = mins(v.gps_last_ping);
    const ssM = mins(v.stationary_since);
    if (spd > cfg.speed_limit) out.push({ v, type: "speeding", icon: Zap, cls: "text-status-recovery", label: `Speeding · ${Math.round(spd)} km/h` });
    if (lastM != null && lastM > cfg.stale_min) out.push({ v, type: "stale", icon: WifiOff, cls: "text-status-overdue", label: `No GPS · ${Math.round(lastM)}m` });
    if (v.ignition === "ON" && spd === 0 && ssM != null && ssM > cfg.idle_min) out.push({ v, type: "idle", icon: Clock, cls: "text-status-overdue", label: `Idle ${Math.round(ssM)}m · ignition ON` });
    if (v.geofence_status === "breach") out.push({ v, type: "breach", icon: AlertTriangle, cls: "text-status-recovery", label: "Out of zone" });
  });
  return out;
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className={`mt-1.5 text-2xl font-bold tracking-tight ${accent || ""}`}>{value}</p>
    </div>
  );
}

export default function LiveBoard() {
  const { ctx, featureEnabled } = useOperator();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [trails, setTrails] = useState({});
  const trailsRef = useRef({});
  const [connected, setConnected] = useState(false);
  const [focus, setFocus] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [follow, setFollow] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const followRef = useRef({ on: false, id: null });
  const esRef = useRef(null);

  // 1s ticker drives the "next auto-sync" countdown
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchNow = async () => {
    setFetching(true);
    try {
      const { data: r } = await api.post("/operator/telematics/fetch-now");
      if (r.status === "ok") toast.success(`Live data refreshed — ${r.record_count} device(s) reporting`);
      else if (r.status === "rate_limited") toast.warning(r.detail || "Vendor rate-limited — try again shortly");
      else toast.error(r.detail || "Could not refresh live data");
      try { const { data: d } = await api.get("/operator/live"); applyData(d); } catch { /* */ }
    } catch (e) { toast.error("Could not refresh live data"); }
    finally { setFetching(false); }
  };

  const telemetryOn = featureEnabled?.("telemetry");

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation([pos.coords.latitude, pos.coords.longitude]),
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  }, []);

  const applyData = (d) => {
    setData(d);
    const next = { ...trailsRef.current };
    (d.vehicles || []).forEach((v) => {
      if (v.lat == null || v.long == null) return;
      const arr = next[v.id] || [];
      const last = arr[arr.length - 1];
      if (!last || last[0] !== v.lat || last[1] !== v.long) {
        next[v.id] = [...arr, [v.lat, v.long]].slice(-60);
      }
    });
    trailsRef.current = next;
    setTrails(next);
    // follow mode: keep map locked on the followed bike as it moves
    if (followRef.current.on && followRef.current.id) {
      const fv = (d.vehicles || []).find((x) => x.id === followRef.current.id);
      if (fv && fv.lat != null) setFocus({ id: fv.id, lat: fv.lat, long: fv.long, _n: Date.now() });
    }
  };

  useEffect(() => {
    if (!telemetryOn) return;
    let pollId;
    const start = async () => {
      try { const { data } = await api.get("/operator/live"); applyData(data); } catch { /* */ }
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/operator/live/stream?token=${getToken()}`;
      const es = new EventSource(url);
      esRef.current = es;
      es.onmessage = (e) => { applyData(JSON.parse(e.data)); setConnected(true); };
      es.onerror = () => {
        setConnected(false); es.close();
        if (!pollId) pollId = setInterval(async () => {
          try { const { data } = await api.get("/operator/live"); applyData(data); } catch { /* */ }
        }, 5000);
      };
    };
    start();
    return () => { esRef.current?.close(); if (pollId) clearInterval(pollId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telemetryOn]);

  if (!ctx) return null;
  if (!telemetryOn) {
    return (
      <div className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center">
        <Lock className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Live telemetry is not enabled for this tenant.</p>
      </div>
    );
  }
  // Wait for the first snapshot before rendering the map — otherwise the map
  // mounts with no hubs and locks its initial view onto a fleet-fit fallback
  // instead of the operator's default hub.
  if (!data) {
    return (
      <div data-testid="live-loading" className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const cfg = { ...DEFAULT_CFG, ...(data?.live_config || {}) };
  const geofence = data?.geofence || { lat: 11.9139, long: 79.8145, radius_km: 8 };
  const hubs = data?.hubs || [];
  const mapDefault = cfg.map_default || "main_hub";
  const initialView = (() => {
    if (mapDefault === "user") return null;
    if (mapDefault === "fleet") return { fleet: true };
    const mainHub = hubs.find((h) => h.is_main) || hubs[0];
    if (mapDefault === "main_hub") {
      return mainHub ? { lat: mainHub.lat, long: mainHub.long, zoom: 15 } : (hubs.length ? null : { fleet: true });
    }
    // a specific id can be either a hub or a pinned vehicle
    const hub = hubs.find((h) => h.id === mapDefault);
    if (hub) return { lat: hub.lat, long: hub.long, zoom: 15 };
    const veh = (data?.vehicles || []).find((v) => v.id === mapDefault && v.lat != null);
    if (veh) return { lat: veh.lat, long: veh.long, zoom: 16 };
    // pinned target not currently available — fall back to the main hub
    return mainHub ? { lat: mainHub.lat, long: mainHub.long, zoom: 15 } : { fleet: true };
  })();
  const rawVehicles = data?.vehicles || [];
  const lastFetched = rawVehicles.map((v) => v.gps_fetched_at).filter(Boolean).sort().slice(-1)[0];
  const pollInterval = data?.poll_interval_seconds || 0;
  // Only show a countdown for rate-limited API providers (long windows); a
  // ~5s simulator window would just be noise.
  let countdownLabel = null;
  if (pollInterval >= 30 && lastFetched) {
    const remaining = Math.max(0, Math.round((new Date(lastFetched).getTime() + pollInterval * 1000 - nowTs) / 1000));
    countdownLabel = remaining > 0 ? `next sync in ${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}` : "syncing…";
  }
  const alerts = cfg.alerts_enabled ? computeAlerts(rawVehicles, cfg) : [];
  const alertIds = new Set(alerts.map((a) => a.v.id));

  let vehicles = [...rawVehicles].sort((a, b) => {
    const ra = activeness(a).rank, rb = activeness(b).rank;
    if (ra !== rb) return ra - rb;
    if ((b.speed || 0) !== (a.speed || 0)) return (b.speed || 0) - (a.speed || 0);
    return (a.reg_number || "").localeCompare(b.reg_number || "");
  });
  if (cfg.search_enabled) {
    const q = query.trim().toLowerCase();
    if (q) vehicles = vehicles.filter((v) => `${v.reg_number} ${v.model} ${v.rider || ""}`.toLowerCase().includes(q));
    if (statusFilter !== "all") vehicles = vehicles.filter((v) => {
      if (statusFilter === "breach") return v.geofence_status === "breach";
      return activeness(v).label.toLowerCase() === statusFilter;
    });
  }

  const breaches = rawVehicles.filter((v) => v.geofence_status === "breach");
  const moving = rawVehicles.filter((v) => (v.speed || 0) > 0).length;
  const idle = rawVehicles.filter((v) => (v.speed || 0) === 0 && v.ignition === "ON").length;
  const speeds = rawVehicles.filter((v) => (v.speed || 0) > 0).map((v) => v.speed);
  const avgSpeed = speeds.length ? Math.round(speeds.reduce((s, x) => s + x, 0) / speeds.length) : 0;
  const charges = rawVehicles.filter((v) => v.charge_pct != null).map((v) => v.charge_pct);
  const avgCharge = charges.length ? Math.round(charges.reduce((s, x) => s + x, 0) / charges.length) : null;

  const focusVehicle = (v) => {
    if (v.lat == null || v.long == null) return;
    if (cfg.follow_enabled && follow) {
      followRef.current = { on: true, id: v.id };
      toast.success(`Following ${v.reg_number} — the map will track it live`);
    }
    setFocus({ id: v.id, lat: v.lat, long: v.long, _n: Date.now() });
  };
  const recenter = () => { if (userLocation) setFocus({ id: "_user", lat: userLocation[0], long: userLocation[1], _n: Date.now() }); };
  const recenterHub = () => {
    const hub = hubs.find((h) => h.is_main) || hubs[0];
    if (hub) setFocus({ id: "_hub", lat: hub.lat, long: hub.long, _n: Date.now() });
  };
  const toggleFollow = () => {
    const on = !follow;
    setFollow(on);
    const id = on ? (focus?.id && !["_user", "_hub"].includes(focus.id) ? focus.id : null) : null;
    followRef.current = { on, id };
    if (on && id) toast.success("Following — the map will track this vehicle live");
    else if (on) toast.info("Follow is on — tap any vehicle to lock the map onto it as it moves");
  };
  const followingReg = follow && focus?.id && !["_user", "_hub"].includes(focus.id)
    ? rawVehicles.find((v) => v.id === focus.id)?.reg_number : null;
  const setMapFocus = async (e, v) => {
    e.stopPropagation();
    const next = (data?.live_config?.map_default === v.id) ? "main_hub" : v.id;
    try {
      await api.patch("/operator/live-config", { ...cfg, map_default: next });
      applyData({ ...data, live_config: { ...(data.live_config || {}), map_default: next } });
      toast.success(next === v.id ? `Map will now open on ${v.reg_number}` : "Map default reset to your hub");
    } catch { toast.error("Could not update the default map view"); }
  };

  const FILTERS = ["all", "moving", "idle", "parked", "breach"];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Live board</h1>
          <p className="mt-1 text-sm text-muted-foreground">Real-time location, heading & status on the map — your fleet only.</p>
        </div>
        <div className="flex flex-col items-start gap-1.5 sm:items-end">
          <div className="flex flex-wrap items-center gap-2">
            {cfg.manual_fetch_enabled && telemetryOn && (
              <button data-testid="fetch-live-now" onClick={fetchNow} disabled={fetching}
                className="inline-flex items-center gap-1.5 rounded-full border border-tenant-border bg-tenant-surface px-3 py-1 text-xs font-semibold text-foreground transition-colors hover:border-tenant-primary hover:text-tenant-primary disabled:opacity-50">
                <RefreshCw className={`h-3.5 w-3.5 ${fetching ? "animate-spin" : ""}`} /> {fetching ? "Fetching…" : "Fetch live data"}
              </button>
            )}
            <span data-testid="live-indicator" className="inline-flex items-center gap-2 rounded-full bg-tenant-primary/10 px-3 py-1 text-xs font-medium text-tenant-primary">
              <span className={`h-2 w-2 rounded-full ${connected ? "animate-pulse bg-status-available" : "bg-status-overdue"}`} />
              {connected ? "Live" : "Reconnecting"}
            </span>
          </div>
          {lastFetched && (
            <span data-testid="feed-synced" className="inline-flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
              <RefreshCw className="h-3 w-3" /> feed synced <span className="font-medium text-foreground">{clock(lastFetched)}</span>
              {countdownLabel && <span data-testid="sync-countdown" className="text-tenant-primary">· {countdownLabel}</span>}
            </span>
          )}
        </div>
      </div>

      {cfg.pulse_enabled && (
        <div data-testid="live-summary" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Stat icon={Bike} label="Fleet" value={rawVehicles.length} />
          <Stat icon={Activity} label="Active now" value={moving + idle} accent="text-status-available" />
          <Stat icon={Gauge} label="Avg speed" value={`${avgSpeed} km/h`} />
          <Stat icon={BatteryMedium} label="Avg battery" value={avgCharge != null ? `${avgCharge}%` : "n/a"} />
          <Stat icon={AlertTriangle} label="Alerts" value={alerts.length} accent={alerts.length ? "text-status-recovery" : ""} />
        </div>
      )}

      {cfg.alerts_enabled && alerts.length > 0 && (
        <div data-testid="alerts-panel" className="rounded-tenant-card border border-status-recovery/30 bg-status-recovery/5 p-4">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-status-recovery"><AlertTriangle className="h-4 w-4" /> {alerts.length} active alert{alerts.length > 1 ? "s" : ""}</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {alerts.map((a, i) => (
              <button key={i} data-testid={`alert-${a.type}-${a.v.reg_number}`} onClick={() => focusVehicle(a.v)}
                className="flex items-center gap-2 rounded-tenant-button border border-tenant-border bg-tenant-surface px-3 py-2 text-left text-sm shadow-tenant-soft transition-all hover:-translate-y-0.5">
                <a.icon className={`h-4 w-4 shrink-0 ${a.cls}`} />
                <span className="truncate"><span className="font-semibold">{a.v.reg_number}</span> · <span className={a.cls}>{a.label}</span></span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="relative lg:col-span-3">
          <FleetMap geofence={geofence} geofencingEnabled={data?.geofencing_enabled} vehicles={rawVehicles}
            trails={trails} focus={focus} userLocation={userLocation} activeId={focus?.id} hubs={hubs} initialView={initialView} />
          <div className="absolute right-4 top-4 z-[400] flex flex-wrap justify-end gap-2">
            {hubs.length > 0 && (
              <button data-testid="recenter-hub-btn" onClick={recenterHub} title="Recenter the map on your main hub"
                className="inline-flex items-center gap-1.5 rounded-full border border-tenant-border bg-tenant-surface/90 px-3 py-1.5 text-xs font-semibold text-foreground shadow-tenant-soft backdrop-blur transition-all hover:-translate-y-0.5 active:scale-95">
                <Home className="h-3.5 w-3.5" /> Hub
              </button>
            )}
            {cfg.follow_enabled && (
              <button data-testid="follow-toggle" onClick={toggleFollow}
                title="Lock the map onto a selected vehicle so it stays centered as the vehicle moves"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold shadow-tenant-soft backdrop-blur transition-all hover:-translate-y-0.5 active:scale-95 ${follow ? "border-tenant-primary bg-tenant-primary text-tenant-primary-foreground" : "border-tenant-border bg-tenant-surface/90 text-foreground"}`}>
                <Target className="h-3.5 w-3.5" /> {follow ? (followingReg ? `Following ${followingReg}` : "Tap a vehicle") : "Follow"}
              </button>
            )}
            {userLocation && (
              <button data-testid="recenter-btn" onClick={recenter} title="Recenter the map on your current location"
                className="inline-flex items-center gap-1.5 rounded-full border border-tenant-border bg-tenant-surface/90 px-3 py-1.5 text-xs font-semibold text-foreground shadow-tenant-soft backdrop-blur transition-all hover:-translate-y-0.5 active:scale-95">
                <Crosshair className="h-3.5 w-3.5" /> My location
              </button>
            )}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Map data &copy; OpenStreetMap · centered on your default hub · tap a vehicle to fly to it · trip trails build live · FleetTrack GPS is polled every ~2 min (vendor rate limit); each card shows its exact fetch time. Real-time push coming soon.
          </p>
        </div>

        <div className="space-y-3 lg:col-span-2">
          {cfg.search_enabled && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="vehicle-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search reg, model or rider…" className="pl-9" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {FILTERS.map((f) => (
                  <button key={f} data-testid={`filter-${f}`} onClick={() => setStatusFilter(f)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors ${statusFilter === f ? "border-tenant-primary bg-tenant-primary/10 text-tenant-primary" : "border-tenant-border text-muted-foreground hover:text-foreground"}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3 lg:max-h-[480px] lg:overflow-y-auto lg:pr-1">
            {vehicles.map((v) => {
              const act = activeness(v);
              return (
                <button key={v.id} data-testid={`live-vehicle-${v.reg_number}`} onClick={() => focusVehicle(v)}
                  className={`w-full rounded-tenant-card border bg-tenant-surface p-4 text-left shadow-tenant-soft transition-all duration-300 ease-spring hover:-translate-y-0.5 hover:shadow-tenant-hover ${alertIds.has(v.id) ? "border-status-recovery/50" : "border-tenant-border hover:border-tenant-primary/30"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{v.reg_number}</p>
                      <p className="text-sm text-muted-foreground">{v.model}{v.rider ? ` · ${v.rider}` : ""}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {user?.role === "owner" && (
                        <span role="button" tabIndex={0} data-testid={`pin-focus-${v.reg_number}`}
                          onClick={(e) => setMapFocus(e, v)}
                          onKeyDown={(e) => { if (e.key === "Enter") setMapFocus(e, v); }}
                          title={data?.live_config?.map_default === v.id ? "This bike is your default map view — click to reset to hub" : "Set as default map view (open the board on this bike)"}
                          className="-m-1 cursor-pointer rounded-full p-1 transition-colors hover:bg-tenant-primary/10">
                          <Star className={`h-4 w-4 ${data?.live_config?.map_default === v.id ? "fill-tenant-primary text-tenant-primary" : "text-muted-foreground/50"}`} />
                        </span>
                      )}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${act.cls}`}>{act.label}</span>
                      {v.geofence_status === "breach" ? (
                        <span className="rounded-full bg-status-recovery/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-status-recovery">Breach</span>
                      ) : v.geofence_status === "inside" ? (
                        <span className="rounded-full bg-status-available/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-status-available">In zone</span>
                      ) : null}
                    </div>
                  </div>
                  {v.address && (
                    <p data-testid={`live-address-${v.reg_number}`} className="mt-2 flex items-start gap-1 text-xs text-muted-foreground">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span className="line-clamp-2">{v.address}</span>
                    </p>
                  )}
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> {Math.round(v.speed || 0)} km/h</span>
                    <span className="flex items-center gap-1"><BatteryMedium className="h-3.5 w-3.5" /> {v.charge_pct != null ? `${v.charge_pct}%` : "—"}</span>
                    <span className="flex items-center gap-1"><Power className="h-3.5 w-3.5" /> {v.ignition || "—"}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {(v.total_km || 0).toLocaleString()} km</span>
                    {v.heading != null && <span className="flex items-center gap-1"><Navigation className="h-3.5 w-3.5" style={{ transform: `rotate(${v.heading}deg)` }} /> {compass(v.heading)}</span>}
                  </div>
                  <div className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                    <p className="flex items-center gap-1"><Radio className="h-3 w-3" /> {v.provider} · last fix {ago(v.gps_last_ping)}</p>
                    {v.gps_fetched_at && (
                      <p data-testid={`fetched-${v.reg_number}`} className="flex items-center gap-1">
                        <RefreshCw className="h-3 w-3" /> data fetched {ago(v.gps_fetched_at)} · <span className="tabular-nums">{clock(v.gps_fetched_at)}</span>
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
            {vehicles.length === 0 && (
              <p className="py-10 text-center text-sm text-muted-foreground">{rawVehicles.length ? "No vehicles match your filter." : <Loader2 className="mx-auto h-5 w-5 animate-spin" />}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
